
import React, { useState, useEffect, useRef } from 'react';
import { Activity, Users, ArrowRight, CheckCircle2, Loader2, Database, LogOut, Bot, Layers } from 'lucide-react';
import { UserRole } from '../types';
import { supabase } from '../services/supabaseClient';
import { useNavigate } from 'react-router-dom';

interface RoleSelectionViewProps {
  onRoleSelected: (role?: UserRole) => Promise<void>;
}

const RoleSelectionView: React.FC<RoleSelectionViewProps> = ({ onRoleSelected }) => {
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const navigate = useNavigate();
  
  // Guard against calling parent update multiple times
  const hasSelectedRole = useRef(false);

  // Safety check: verify if user actually already has a role to prevent getting stuck
  useEffect(() => {
    let mounted = true;
    const checkExistingRole = async () => {
        if (hasSelectedRole.current) return;

        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
                const { data, error } = await supabase.from('users').select('role').eq('id', user.id).maybeSingle();
                
                if (error) {
                  console.warn("Error checking role (RoleSelectionView):", error.message);
                }

                if (data && data.role && mounted) {
                    console.log("Role already exists, restoring...", data.role);
                    hasSelectedRole.current = true;
                    setCheckingStatus(false); // Stop loading indicator while we switch
                    // Trigger the parent update cleanly
                    await onRoleSelected(data.role as UserRole);
                    return; 
                }
            }
        } catch (e) {
            console.error("Error checking existing role", e);
        } finally {
            if (mounted) setCheckingStatus(false);
        }
    };
    
    // Increased delay to 1s to ensure App.tsx has settled its initial checks
    // This prevents race conditions where both try to set the role simultaneously
    const timer = setTimeout(checkExistingRole, 1000);
    
    return () => { 
        mounted = false; 
        clearTimeout(timer);
    };
  }, [onRoleSelected]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    window.location.href = '/';
  };

  // Roles available for selection
  const roles = [
    {
      id: UserRole.RESEARCHER,
      title: 'Researcher',
      description: 'I analyze market trends, correlations, and aggregated datasets.',
      icon: <Activity className="w-8 h-8 text-emerald-400" />,
      color: 'border-emerald-500/50 hover:border-emerald-500'
    },
    {
      id: UserRole.GENERAL_USER,
      title: 'General User / Earner',
      description: 'I participate in surveys and earn rewards for contributing local insights.',
      icon: <Users className="w-8 h-8 text-blue-400" />,
      color: 'border-blue-500/50 hover:border-blue-500'
    },
    {
      id: UserRole.AI_AGENT,
      title: 'AI Agent (x402)',
      description: 'Autonomous agents performing surveys via API/Protocol (ERC-8004 compatible).',
      icon: <Bot className="w-8 h-8 text-purple-400" />,
      color: 'border-purple-500/50 hover:border-purple-500'
    }
  ];

  const handleConfirm = async () => {
    if (!selectedRole || isSubmitting) return;
    setIsSubmitting(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) throw new Error("No authenticated user found");

      // Use upsert to handle both creation (if missing) and update (if exists)
      const { error } = await supabase
        .from('users')
        .upsert({ 
            id: user.id,
            role: selectedRole,
            email: user.email,
            username: user.user_metadata?.full_name 
        }, { onConflict: 'id' });

      if (error) {
        throw error;
      }
      
      if (!hasSelectedRole.current) {
         hasSelectedRole.current = true;
         await onRoleSelected(selectedRole);
      }
      
    } catch (error: any) {
      console.error('Error updating role:', error);
      alert(`Failed to save role: ${error.message || 'Unknown error'}`);
      setIsSubmitting(false);
    }
  };

  if (checkingStatus) {
      return (
          <div className="min-h-screen bg-slate-900 flex items-center justify-center">
              <div className="flex flex-col items-center gap-4">
                  <Loader2 className="animate-spin text-indigo-500" size={32} />
                  <p className="text-slate-500 text-sm">Verifying profile...</p>
              </div>
          </div>
      );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-4 relative">
      {/* Top Bar for Sign Out */}
      <div className="absolute top-4 right-4 z-10">
        <button 
          onClick={handleSignOut}
          className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-red-900/30 text-slate-400 hover:text-red-400 rounded-lg border border-slate-700 transition-colors text-sm font-medium"
        >
          <LogOut size={16} /> Sign Out
        </button>
      </div>

      <div className="max-w-5xl w-full space-y-8 animate-in fade-in duration-500">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-white mb-2">Choose your Workspace</h1>
          <p className="text-slate-400">Select your primary role to customize your Tinaney experience.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mx-auto">
          {roles.map((role) => (
            <button
              key={role.id}
              onClick={() => setSelectedRole(role.id)}
              className={`relative p-6 rounded-xl border-2 bg-slate-800 text-left transition-all duration-200 group flex flex-col ${
                selectedRole === role.id ? 'border-indigo-500 shadow-xl shadow-indigo-500/10 scale-[1.02]' : 'border-slate-700 hover:bg-slate-750 ' + role.color
              }`}
            >
              {selectedRole === role.id && (
                <div className="absolute top-4 right-4">
                  <CheckCircle2 className="text-indigo-500" size={24} />
                </div>
              )}
              <div className="mb-4 bg-slate-900 w-16 h-16 rounded-lg flex items-center justify-center border border-slate-700 group-hover:border-slate-600 transition-colors">
                {role.icon}
              </div>
              <h3 className="text-xl font-bold text-white mb-2">{role.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed flex-1">{role.description}</p>
            </button>
          ))}
        </div>

        <div className="flex justify-center pt-8">
          <button
            onClick={handleConfirm}
            disabled={!selectedRole || isSubmitting}
            className={`flex items-center gap-2 px-8 py-4 rounded-full font-bold text-lg transition-all ${
              selectedRole && !isSubmitting
                ? 'bg-white text-indigo-900 hover:bg-indigo-50 shadow-lg hover:shadow-indigo-500/20 transform hover:-translate-y-1'
                : 'bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700'
            }`}
          >
            {isSubmitting ? 'Setting up...' : 'Enter Platform'}
            {isSubmitting ? <Loader2 className="ml-2 animate-spin" size={20} /> : <ArrowRight size={20} />}
          </button>
        </div>
      </div>
      
      {/* Dev / Support Link for Database Setup */}
      <div className="absolute bottom-6 right-6 flex gap-4">
        <button 
            onClick={() => navigate('/schema')}
            className="flex items-center gap-2 text-xs text-slate-600 hover:text-slate-400 transition-colors"
        >
            <Database size={12} /> Database Setup
        </button>
        <button 
            onClick={() => navigate('/smart-contract')}
            className="flex items-center gap-2 text-xs text-slate-600 hover:text-slate-400 transition-colors"
        >
            <Layers size={12} /> Smart Contract
        </button>
      </div>
    </div>
  );
};

export default RoleSelectionView;
